import { useEffect, useMemo, useState } from 'react';
import { Puzzle } from '../../shared/types';
import { RuleId, sigKey } from '../../shared/rules';
import { DeductionGraph, Support, buildGraph } from '../../shared/analysis/graph';
import { FactId, factCol, factRow, isQueenFact } from '../../shared/analysis/facts';
import { easiestSupport, profileGraph, requiredRules, requiredTechniques, ruleDifficulty } from '../../shared/analysis/profile';
import { ALL_RULE_IDS, RULE_COLORS, RULE_NAMES } from '../../shared/analysis/rulemeta';
import { CellState, createGameState } from '../game';
import { HintHighlight } from '../hints';
import { Board } from './Board';

// The deduction graph, made visible.
//
// The layered DAG on the right is the easiest route the rule engine can take to each fact;
// the board on the left shows what the selected deduction is actually looking at. Nodes are
// *supports* (one deduction yielding one fact) rather than raw facts — fact-level is the right
// substrate for computation but far too dense to read.

interface Props {
  puzzle: Puzzle;
  onBack: () => void;
  /** Step to the adjacent puzzle within the same category, staying in the explorer. */
  onPrev?: () => void;
  onNext?: () => void;
}

interface Node {
  support: Support;
  x: number;
  y: number;
  /** Its fact has no other support at all: this deduction is unavoidable. */
  onlyWay: boolean;
}

const NODE_R = 7;
const COL_W = 150;
const ROW_H = 26;
const PAD = 26;

export function AnalysisView({ puzzle, onBack, onPrev, onNext }: Props) {
  const [selected, setSelected] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  // Starts at 0 (empty board) so opening the analyzer never spoils an unsolved puzzle.
  const [round, setRound] = useState<number>(0);

  // The graph itself is quick (~70-500ms). The ablation pass that answers "which rules are
  // strictly required" is 9 more closures — up to ~4s on a 12x12 — so it is computed after the
  // first paint instead of blocking it.
  const { graph, structural } = useMemo(() => {
    const g = buildGraph(puzzle.colorRegions);
    return { graph: g, structural: profileGraph(g) };
  }, [puzzle.id]);

  const [ablation, setAblation] = useState<
    { required: RuleId[]; costOfAvoiding: Partial<Record<RuleId, number | null>> } | null
  >(null);

  useEffect(() => { setSelected(null); setHovered(null); setRound(0); setAblation(null); }, [puzzle.id]);

  useEffect(() => {
    let live = true;
    // Yield to the browser first so the board and graph are on screen before this runs.
    const t = setTimeout(() => {
      const result = requiredRules(puzzle.colorRegions);
      if (live) setAblation(result);
    }, 30);
    return () => { live = false; clearTimeout(t); };
  }, [puzzle.id]);

  // Lay the supports out in columns by round. Only the *easiest* route to each fact is shown —
  // every fact typically has several redundant supports, and drawing all of them buries the
  // actual shape of the solve under a hairball (it was a toggle; it was slow and told you
  // nothing, so the graph is now always the easiest-route view).
  const { nodes, byId, maxRound, height } = useMemo(() => {
    const primaryIds = new Set<number>();
    const onlyWayIds = new Set<number>();
    for (const f of graph.facts) {
      if (!f.derived || f.bookkeeping || f.supports.length === 0) continue;
      const best = easiestSupport(graph, f.supports);
      if (best) primaryIds.add(best.id);
      if (f.supports.length === 1) onlyWayIds.add(f.supports[0]);
    }
    const chosen = graph.supports.filter(s => primaryIds.has(s.id));

    const columns = new Map<number, Support[]>();
    for (const s of chosen) {
      const arr = columns.get(s.round) ?? columns.set(s.round, []).get(s.round)!;
      arr.push(s);
    }
    const nodes: Node[] = [];
    let tallest = 0;
    for (const [r, arr] of columns) {
      arr.sort((a, b) => ruleDifficulty(a.sig.rule) - ruleDifficulty(b.sig.rule) || a.sig.rule.localeCompare(b.sig.rule));
      tallest = Math.max(tallest, arr.length);
      arr.forEach((s, i) => nodes.push({
        support: s,
        x: PAD + (r - 1) * COL_W,
        y: PAD + i * ROW_H,
        onlyWay: onlyWayIds.has(s.id),
      }));
    }
    return {
      nodes,
      byId: new Map(nodes.map(n => [n.support.id, n])),
      maxRound: Math.max(1, ...chosen.map(s => s.round)),
      height: PAD * 2 + tallest * ROW_H,
    };
  }, [graph]);

  // A → B when a fact A establishes is one of B's minimal premises: "this unlocks that".
  const edges = useMemo(() => {
    const yielders = new Map<FactId, number[]>();
    for (const n of nodes) {
      for (const f of n.support.yields) {
        (yielders.get(f) ?? yielders.set(f, []).get(f)!).push(n.support.id);
      }
    }
    const out: Array<{ from: number; to: number }> = [];
    for (const n of nodes) {
      const seen = new Set<number>();
      for (const p of n.support.premises) {
        for (const src of yielders.get(p) ?? []) {
          if (src === n.support.id || seen.has(src)) continue;
          // keep it readable: only draw the edge from the immediately preceding layer
          if (byId.get(src)!.support.round !== n.support.round - 1) continue;
          seen.add(src);
          out.push({ from: src, to: n.support.id });
        }
      }
    }
    return out;
  }, [nodes, byId]);

  const focusId = hovered ?? selected;
  const focus = focusId !== null ? byId.get(focusId) ?? null : null;

  // Focusing a deduction shows the board as it stands when that deduction becomes available —
  // i.e. exactly the state its premises describe. Much more informative than pinning the board
  // to wherever the scrubber happens to sit. With nothing focused, the scrubber wins.
  const shownRound = focus ? focus.support.round - 1 : round;

  const boardAt = useMemo(() => {
    const size = puzzle.size;
    const board: CellState[][] = Array.from({ length: size }, () => Array<CellState>(size).fill(null));
    for (const f of graph.facts) {
      if (!f.derived || f.round > shownRound) continue;
      board[f.r][f.c] = f.kind === 'queen' ? 'queen' : 'X';
    }
    return board;
  }, [graph, shownRound, puzzle.size]);

  const gameState = useMemo(() => ({ ...createGameState(puzzle), board: boardAt }), [puzzle, boardAt]);

  // Reuse the hint highlighting the board already knows how to render.
  const highlight: HintHighlight | null = useMemo(() => {
    if (!focus) return null;
    const s = focus.support;
    const rows = new Set<number>(), cols = new Set<number>(), regions = new Set<string>();
    for (const u of s.units) {
      if (u.type === 'row') rows.add(u.index);
      else if (u.type === 'col') cols.add(u.index);
      else regions.add(u.color);
    }
    const target = s.yields[0];
    const isPlace = target !== undefined && isQueenFact(target, puzzle.size);
    const targetKey = target !== undefined ? `${factRow(target, puzzle.size)},${factCol(target, puzzle.size)}` : null;
    return {
      cause: new Set(s.causeCells),
      elim: isPlace || !targetKey ? new Set<string>() : new Set([targetKey]),
      place: isPlace && targetKey ? new Set([targetKey]) : new Set<string>(),
      rows, cols, regions,
    };
  }, [focus, puzzle.size]);

  const spectrum = Object.entries(structural.techniqueSpectrum).sort((a, b) => b[1] - a[1]);
  const usedRules = [...new Set(nodes.map(n => n.support.sig.rule))].sort();
  const width = PAD * 2 + maxRound * COL_W;

  return (
    <div className="analysis-view">
      <div className="analysis-header">
        <button className="btn" onClick={onBack}>← Back</button>
        <h2>{puzzle.name} <span className="analysis-dim">· {puzzle.size}×{puzzle.size}</span></h2>
        <div className="analysis-nav">
          <button className="btn-nav" onClick={onPrev} disabled={!onPrev} title="Previous puzzle">
            <span className="show-mobile">◀</span><span className="hide-mobile">Prev</span>
          </button>
          <button className="btn-nav" onClick={onNext} disabled={!onNext} title="Next puzzle">
            <span className="show-mobile">▶</span><span className="hide-mobile">Next</span>
          </button>
        </div>
      </div>

      <div className="analysis-stats">
        {puzzle.difficulty && (
          <Stat
            label="Difficulty"
            value={`${Math.round(puzzle.difficulty.refSolveMs / 1000)}s`}
            hint={
              'Predicted solve time for a typical player — reasoning and clicking together, which is '
              + `the difficulty scalar. Band: ${puzzle.difficulty.band}. `
              + `Of that, about ${Math.round(puzzle.difficulty.refDeductionMs / 1000)}s is the simulated `
              + 'search itself, before the calibration that maps the simulator onto real solve times. '
              + 'The calibration is fitted against every puzzle with 3+ distinct players and lands '
              + 'within ~23% of the median player, so read this as a ballpark, not a stopwatch.'
            }
          />
        )}
        <Stat label="Depth" value={String(structural.depth)} hint="Rounds of deduction the puzzle needs" />
        <Stat label="Bottlenecks" value={String(structural.bottlenecks.length)} hint="Rounds yielding ≤2 facts — the moments it resists" />
        <Stat label="Forced steps" value={String(structural.singleSupportFacts)} hint="Facts reachable exactly one way" />
        <Stat label="Deductions" value={String(structural.supports)} hint="Distinct rule applications available" />
        {/* No "Requires" stat: the rules overlap so heavily that almost every puzzle has an
            alternative route for every step, so it read "Nothing special" nearly always. The
            ablation still runs — it is what "Technique roles" below is built from, where a
            required rule is worth a line and the empty case is one quiet sentence. */}
        {!structural.solved && <Stat label="⚠" value="Unsolvable" hint="The rule engine cannot finish this puzzle" />}
      </div>

      <div className="analysis-body">
        <div className="analysis-left">
          <Board puzzle={puzzle} state={gameState} onStateChange={() => {}} readOnly highlight={highlight} />

          <div className="analysis-scrub">
            <label>
              {focus
                ? <>Showing the board as of round <strong>{shownRound}</strong> — where this deduction becomes available</>
                : <>After round <strong>{Math.min(round, structural.depth)}</strong> of {structural.depth}</>}
            </label>
            <input
              type="range" min={0} max={structural.depth} value={Math.min(round, structural.depth)}
              onChange={e => setRound(Number(e.target.value))}
            />
          </div>

          {focus ? (
            <div className="analysis-detail">
              <div className="analysis-detail-title">
                <span className="rule-dot" style={{ background: RULE_COLORS[focus.support.sig.rule] }} />
                {RULE_NAMES[focus.support.sig.rule]}
                <span className="analysis-dim"> · {sigKey(focus.support.sig)}</span>
              </div>
              <div className="analysis-dim">
                round {focus.support.round} · needs {focus.support.premises.length} earlier fact{focus.support.premises.length === 1 ? '' : 's'}
                {focus.onlyWay && <strong className="analysis-only"> · only way to reach this cell</strong>}
              </div>
            </div>
          ) : (
            <div className="analysis-detail analysis-dim">Hover a node to see what it reasons about.</div>
          )}

          {ablation && (() => {
            // "Helps but isn't needed" — the rules that shorten the solve without being
            // load-bearing. This is what explains seeing e.g. Paired shadow hints on a puzzle
            // you finished without ever needing one.
            const shortcuts = ALL_RULE_IDS
              .filter(r => r !== 'R8' && (ablation.costOfAvoiding[r] ?? 0) > 0)
              .sort((a, b) => (ablation.costOfAvoiding[b] ?? 0) - (ablation.costOfAvoiding[a] ?? 0));
            const req = requiredTechniques(ablation.required);
            return (
              <div className="analysis-shortcuts">
                <div className="analysis-sub">Technique roles</div>
                {req.length === 0 && (
                  <div className="analysis-dim analysis-note">
                    No elimination technique is strictly required — every step has an alternative route,
                    so this puzzle can be finished without ever using a particular rule.
                  </div>
                )}
                {req.map(r => (
                  <div key={r} className="analysis-role">
                    <span className="rule-dot" style={{ background: RULE_COLORS[r] }} />
                    <strong>{RULE_NAMES[r]}</strong>
                    <span className="analysis-dim">— required; no way around it</span>
                  </div>
                ))}
                {shortcuts.map(r => (
                  <div key={r} className="analysis-role">
                    <span className="rule-dot" style={{ background: RULE_COLORS[r] }} />
                    {RULE_NAMES[r]}
                    <span className="analysis-dim">
                      — optional shortcut; without it the solve takes {ablation.costOfAvoiding[r]} more round
                      {ablation.costOfAvoiding[r] === 1 ? '' : 's'}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="analysis-legend">
            {usedRules.map(r => (
              <span key={r} className="analysis-legend-item">
                <span className="rule-dot" style={{ background: RULE_COLORS[r] }} />{r} {RULE_NAMES[r]}
              </span>
            ))}
          </div>

          {spectrum.length > 0 && (
            <div className="analysis-spectrum">
              <div className="analysis-sub">Technique spectrum</div>
              {spectrum.slice(0, 8).map(([k, n]) => (
                <div key={k} className="analysis-bar-row">
                  <span className="analysis-bar-label">{k}</span>
                  <span className="analysis-bar" style={{
                    width: `${(100 * n) / spectrum[0][1]}%`,
                    background: RULE_COLORS[k.slice(0, 2) as RuleId] ?? '#888',
                  }} />
                  <span className="analysis-dim">{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="analysis-right">
          <div className="analysis-graph-scroll">
            <svg width={width} height={height} className="analysis-graph">
              {Array.from({ length: maxRound }, (_, i) => (
                <text key={i} x={PAD + i * COL_W} y={14} className="analysis-round-label">round {i + 1}</text>
              ))}
              {edges.map((e, i) => {
                const a = byId.get(e.from)!, b = byId.get(e.to)!;
                const active = focusId !== null && (e.from === focusId || e.to === focusId);
                return (
                  <path
                    key={i}
                    d={`M${a.x + NODE_R} ${a.y} C${a.x + COL_W / 2} ${a.y}, ${b.x - COL_W / 2} ${b.y}, ${b.x - NODE_R} ${b.y}`}
                    className={active ? 'analysis-edge active' : 'analysis-edge'}
                  />
                );
              })}
              {nodes.map(n => {
                const dim = focusId !== null && focusId !== n.support.id
                  && !edges.some(e => (e.from === focusId && e.to === n.support.id) || (e.to === focusId && e.from === n.support.id));
                return (
                  <circle
                    key={n.support.id}
                    cx={n.x} cy={n.y}
                    r={n.onlyWay ? NODE_R + 1.5 : NODE_R}
                    fill={RULE_COLORS[n.support.sig.rule]}
                    className={`analysis-node${dim ? ' dim' : ''}${selected === n.support.id ? ' selected' : ''}${n.onlyWay ? ' onlyway' : ''}`}
                    onMouseEnter={() => setHovered(n.support.id)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => setSelected(selected === n.support.id ? null : n.support.id)}
                  >
                    <title>{`${RULE_NAMES[n.support.sig.rule]} (${sigKey(n.support.sig)})\nround ${n.support.round}, ${n.support.premises.length} premises`}</title>
                  </circle>
                );
              })}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="analysis-stat" title={hint}>
      <div className="analysis-stat-value">{value}</div>
      <div className="analysis-stat-label">{label}</div>
    </div>
  );
}
